import { Building } from "../../building/Building";
import { BoxGeometry, Mesh, Material, TextureLoader, RepeatWrapping, MeshStandardMaterial, Texture, SRGBColorSpace } from "three";
import { Main } from "../../Main";

export class ConcreteSlab
{
	private _building : Building;

	private _material : Material;
	private _mesh : Mesh;

	private _left  : Mesh;
	private _right : Mesh;
	private _front : Mesh;
	private _back  : Mesh;

	constructor(building:Building)
	{
		this._building = building;
		this.init();
	}

	private init()
	{
		var geometry = new BoxGeometry(1, 1, 1);

		this._left  = new Mesh(geometry, this.createMaterial());
		this._right = new Mesh(geometry, this.createMaterial());
		this._front = new Mesh(geometry, this.createMaterial());
		this._back  = new Mesh(geometry, this.createMaterial());

		this._left.receiveShadow = true;
		this._right.receiveShadow = true;
		this._front.receiveShadow = true;
		this._back.receiveShadow = true;

		this._building.scene3D.add(this._left);
		this._building.scene3D.add(this._right);
		this._building.scene3D.add(this._front);
		this._building.scene3D.add(this._back);

		this._left.rotation.x  =
		this._right.rotation.x =
		this._front.rotation.x =
		this._back.rotation.x  = -Math.PI/2;

		this._left.scale.z  =
		this._right.scale.z =
		this._front.scale.z =
		this._back.scale.z  = 0.2;

		var buildingConfig = Main.getInstance().config.building;

		buildingConfig.sizeChange.add(this.update);

		buildingConfig.leanto.back.enabled.signal.add(this.update);
		buildingConfig.leanto.front.enabled.signal.add(this.update);
		buildingConfig.leanto.left.enabled.signal.add(this.update);
		buildingConfig.leanto.right.enabled.signal.add(this.update);

		buildingConfig.leanto.back.length.signal.add(this.update);
		buildingConfig.leanto.front.length.signal.add(this.update);
		buildingConfig.leanto.left.length.signal.add(this.update);
		buildingConfig.leanto.right.length.signal.add(this.update);

		buildingConfig.no_walls.signal.add(this.update);

		buildingConfig.insetBay.back.count.signal.add(this.update);
		buildingConfig.insetBay.front.count.signal.add(this.update);
		buildingConfig.insetBay.back.length_1.signal.add(this.update);
		buildingConfig.insetBay.back.length_2.signal.add(this.update);
		buildingConfig.insetBay.front.length_1.signal.add(this.update);
		buildingConfig.insetBay.front.length_2.signal.add(this.update);

		this.update();
	}

	private createMaterial()
	{
		var map     = new TextureLoader().load("assets/concrete/ConcreteBare0350_7_S.jpg", () => Main.getInstance().render = true);
		var bumpMap = new TextureLoader().load("assets/concrete/ConcreteBare0350_7_S_bump.png", () => Main.getInstance().render = true);

		map.colorSpace = SRGBColorSpace;

		////map.repeat.set(10, 10);
		map.wrapS = map.wrapT = RepeatWrapping;
		///map.format = RGBFormat;

		//console.log(Main.getInstance().content.maxAnisotropy);
		bumpMap.anisotropy = 4; //Main.getInstance().content.maxAnisotropy;

		////bumpMap.repeat.set( 10, 10 );


		//bumpMap.offset.set( 0.001, 0.001 );
		bumpMap.wrapS = bumpMap.wrapT = RepeatWrapping;
		//bumpMap.format = RGBFormat;
		//bumpMap.minFilter = LinearMipMapLinearFilter;

		var material = new MeshStandardMaterial({
			map: map,
			//Xambient: 0x000000,
			//Xcolor: 0x000000,
			envMap: Main.getInstance().content.scene3D.background as Texture,
			bumpMap: bumpMap,
			bumpScale: 1,
			roughness: 1,
			metalness: 0,
			//metal: true
		});

		return material;
	}

	// called when mesh
	private update = ():void =>
	{
		var leanToConfig = Main.getInstance().config.building.leanto;
		var insetBayConfig = Main.getInstance().config.building.insetBay;
		var metrics = this._building.config.metrics;
		var margin = 8;
		var margin_eps = 0.01; // otherwise concrete is z-fighting with the walls

		// side: left, right

		var leanToLeft  = leanToConfig.left.getRealLength();
		var leanToRight = leanToConfig.right.getRealLength();
		var leanToFront = leanToConfig.front.getRealLength();
		var leanToBack  = leanToConfig.back.getRealLength();

		var insetBayFront = 0;
		var offset = 8.05;
		if (insetBayConfig.front.count.value === 1)
		{
			insetBayFront = insetBayConfig.front.length_1.value - offset;
		}
		else if (insetBayConfig.front.count.value === 2)
		{
			insetBayFront = insetBayConfig.front.length_1.value + insetBayConfig.front.length_2.value - offset;
		}

		var insetBayBack = 0;
		if (insetBayConfig.back.count.value === 1)
		{
			insetBayBack = insetBayConfig.back.length_1.value - offset;
		}
		else if (insetBayConfig.back.count.value === 2)
		{
			insetBayBack = insetBayConfig.back.length_1.value + insetBayConfig.back.length_2.value - offset;
		}

		var leftWidth  = leanToLeft + margin;
		var rightWidth = leanToRight + margin;
		var sideLength = metrics.length + insetBayFront + insetBayBack + margin * 2 + leanToFront + leanToBack;
		var sidePosZ   = (leanToFront - leanToBack) / 2 + (insetBayFront - insetBayBack) / 2;

		this._left.position.x = -metrics.width_half - leftWidth/2 - margin_eps; //leftEdge - margin/2 - margin_eps;
		this._left.position.z = sidePosZ;
		this._left.scale.x = leftWidth;
		this._left.scale.y = sideLength;

		this._right.position.x = metrics.width_half + rightWidth/2 + margin_eps;
		this._right.position.z = sidePosZ;
		this._right.scale.x = rightWidth;
		this._right.scale.y = sideLength;

		// end: front, back

		var frontHeight = margin + leanToFront + insetBayFront;
		var backHeight = margin + leanToBack + insetBayBack;

		this._front.position.z = metrics.length_half + frontHeight/2 + margin_eps;
		this._front.scale.x = metrics.width;
		this._front.scale.y = frontHeight;

		if (Main.getInstance().config.building.no_walls.value)
		{
			// no walls -> no floor -> fill in with concrete
			backHeight = margin + leanToBack + metrics.length;
			this._back.position.z = -(margin + leanToBack)/2  - margin_eps;
			this._back.scale.x = metrics.width;
			this._back.scale.y = backHeight;
		}
		else
		{
			this._back.position.z = -metrics.length_half - backHeight/2 - margin_eps;
			this._back.scale.x = metrics.width;
			this._back.scale.y = backHeight;
		}

		// repeat

		this.setRepeat(this._left);
		this.setRepeat(this._right);
		this.setRepeat(this._front);
		this.setRepeat(this._back);
	};

	public needsUpdate()
	{
		(<Material>this._left.material).needsUpdate = true;
		(<Material>this._right.material).needsUpdate = true;
		(<Material>this._front.material).needsUpdate = true;
		(<Material>this._back.material).needsUpdate = true;
	}

	private setRepeat(mesh:Mesh)
	{
		var repeatDivider = 7.0;

		var mat = <MeshStandardMaterial>mesh.material;

		var repeatX = mesh.scale.x / repeatDivider;
		var repeatY = mesh.scale.y / repeatDivider;
		mat.map.repeat.set(repeatX, repeatY);
		mat.bumpMap.repeat.set(repeatX, repeatY);
	}
}